/**
 * HealthMatrix — the CT health table, shared by Brand Health and Courier Health.
 * Columns = stages (Handover Failed / Delivery / Pickup Pending / RTO) + Net SLA + Action.
 * One block per entity (a brand, or a courier); each entity's state rows expand to
 * their sub-breakdown (ageing buckets, courier accounts, …).
 *
 * entities: [{
 *   name, tier?, criticalCount, totalCount, score,
 *   columns: [{label, score, criticalCount, totalCount}],   // labels shared across entities
 *   rows:    [{key, label, color, cells:[{count,pct}], children?:[{label, cells}]}],
 * }]
 */
import React, {useState, useMemo} from 'react'
import {Box, Typography, Collapse, TextField, InputAdornment, Button} from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import SearchIcon from '@mui/icons-material/Search'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import {HealthRing, StackedBar, scoreColor, cellText, fmt, columnStates} from './healthShared'
import {FONT, SIZE, WEIGHT} from '../../styles/foundation/typography'
import {NAVY, BORDER, SUBTLE, WHITE, ERROR, PRIMARY_BLUE} from '../../styles/foundation/colors'
import {SHADOW} from '../../styles/foundation/effects'

const SORTS = [
  {key: 'open', label: 'Open shipments'},
  {key: 'critical', label: 'Critical shipments'},
]

export default function HealthMatrix({entities = [], entityHeader = 'Brand', onView}) {
  const [open, setOpen] = useState({})
  const [query, setQuery] = useState('')
  const [sortBy, setSortBy] = useState('critical')
  const toggle = (k) => setOpen((o) => ({...o, [k]: !o[k]}))

  const multi = entities.length > 1
  const view = useMemo(() => {
    let list = entities
    if (query.trim()) {
      const q = query.trim().toLowerCase()
      list = list.filter((e) => (e.name || '').toLowerCase().includes(q))
    }
    if (multi) {
      list = [...list].sort((a, b) =>
        sortBy === 'critical'
          ? b.criticalCount - a.criticalCount
          : (b.openCount || 0) - (a.openCount || 0),
      )
    }
    return list
  }, [entities, query, sortBy, multi])

  const cols = entities[0]?.columns || []
  const grid = `minmax(200px, 1.4fr) repeat(${cols.length}, minmax(0, 1fr)) 84px 80px`

  const colTitleSx = {fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold, color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.03em'}
  const cellSx = {fontFamily: FONT, fontSize: SIZE.xs, color: NAVY, textAlign: 'center'}

  return (
    <Box>
      {/* Filter Reference toolbar — search + sort (only useful with multiple rows) */}
      {multi && (
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 1.5}}>
          <TextField
            size='small'
            placeholder={`Search ${entityHeader.toLowerCase()}…`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{minWidth: 240, bgcolor: WHITE, '& .MuiInputBase-input': {fontFamily: FONT, fontSize: SIZE.xs}}}
            InputProps={{startAdornment: (<InputAdornment position='start'><SearchIcon sx={{fontSize: 18, color: SUBTLE}} /></InputAdornment>)}}
          />
          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
            <Typography sx={{...colTitleSx, color: SUBTLE}}>Sort by</Typography>
            {SORTS.map((s) => {
              const on = sortBy === s.key
              return (
                <Box key={s.key} onClick={() => setSortBy(s.key)}
                  sx={{
                    px: 1.5, py: 0.6, borderRadius: '999px', cursor: 'pointer', userSelect: 'none',
                    fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold,
                    border: `1px solid ${on ? PRIMARY_BLUE : BORDER}`,
                    bgcolor: on ? PRIMARY_BLUE : WHITE, color: on ? WHITE : NAVY,
                    transition: 'all 0.15s', '&:hover': {borderColor: PRIMARY_BLUE},
                  }}>
                  {s.label}
                </Box>
              )
            })}
          </Box>
        </Box>
      )}

      <Box sx={{bgcolor: WHITE, borderRadius: '12px', boxShadow: SHADOW.card, border: `1px solid ${BORDER}`, overflow: 'hidden'}}>
        {/* Column titles */}
        <Box sx={{display: 'grid', gridTemplateColumns: grid, alignItems: 'center', gap: 1.5, px: 2.5, py: 1.25, bgcolor: '#f8f9fc', borderBottom: `1px solid ${BORDER}`}}>
          <Typography sx={colTitleSx}>{entityHeader}</Typography>
          {cols.map((col) => (
            <Box key={col.label} sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5}}>
              <Typography sx={{...colTitleSx, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{col.label}</Typography>
              <FilterListIcon sx={{fontSize: 14, color: SUBTLE, flexShrink: 0}} />
            </Box>
          ))}
          <Typography sx={{...colTitleSx, textAlign: 'center'}}>Net SLA</Typography>
          <Typography sx={{...colTitleSx, textAlign: 'center'}}>Action</Typography>
        </Box>

        {view.length === 0 && (
          <Box sx={{p: 4, textAlign: 'center'}}>
            <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE}}>No matches for “{query}”.</Typography>
          </Box>
        )}

        {view.map((e, ei) => (
          <Box key={e.name} sx={{borderBottom: ei < view.length - 1 ? `2px solid ${BORDER}` : 'none'}}>
            {/* Entity summary row */}
            <Box sx={{display: 'grid', gridTemplateColumns: grid, alignItems: 'center', gap: 1.5, px: 2.5, py: 2, borderBottom: `1px solid ${BORDER}`}}>
              <Box>
                <Typography sx={{fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.bold, color: NAVY}}>{e.name}</Typography>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25, mt: 0.4, flexWrap: 'wrap'}}>
                  {/* Both sort metrics are shown so the active sort's ranking is self-evident;
                      the one currently sorted on is emphasised. */}
                  {multi && (
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                      <Box sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: PRIMARY_BLUE}} />
                      <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: sortBy === 'open' ? NAVY : SUBTLE, fontWeight: sortBy === 'open' ? WEIGHT.bold : WEIGHT.regular}}>
                        {fmt(e.openCount || 0)} open
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                    <Box sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: ERROR}} />
                    <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: sortBy === 'critical' ? NAVY : SUBTLE, fontWeight: sortBy === 'critical' ? WEIGHT.bold : WEIGHT.regular}}>
                      {fmt(e.criticalCount)} critical
                    </Typography>
                  </Box>
                  <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}}>of {fmt(e.totalCount)}</Typography>
                  {e.tier && (
                    <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}}>
                      <Box component='span' sx={{color: BORDER, mr: 0.75}}>|</Box>
                      <Box component='span' sx={{fontWeight: WEIGHT.bold}}>{e.tier}</Box>
                    </Typography>
                  )}
                </Box>
              </Box>

              {e.columns.map((col, j) => (
                <Box key={col.label} sx={{textAlign: 'center'}}>
                  <Typography sx={{fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.extra, color: scoreColor(col.score), mb: 0.6}}>{col.score}%</Typography>
                  <StackedBar states={columnStates(e.rows, j)} />
                  <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: NAVY, fontWeight: WEIGHT.semibold, mt: 0.6}}>
                    {fmt(col.criticalCount)} of {fmt(col.totalCount)} Critical
                  </Typography>
                </Box>
              ))}

              <Box sx={{display: 'flex', justifyContent: 'center'}}><HealthRing score={e.score} /></Box>
              <Box sx={{display: 'flex', justifyContent: 'center'}}>
                {onView && (
                  <Button
                    size='small'
                    onClick={() => onView(e)}
                    startIcon={<VisibilityOutlinedIcon sx={{fontSize: 15}} />}
                    sx={{
                      textTransform: 'none', fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold,
                      color: PRIMARY_BLUE, border: `1px solid ${BORDER}`, borderRadius: '8px', px: 1, py: 0.4,
                      '&:hover': {borderColor: PRIMARY_BLUE, bgcolor: '#f8faff'},
                    }}
                  >
                    View
                  </Button>
                )}
              </Box>
            </Box>

            {/* State rows (expandable) */}
            {e.rows.map((r, i) => {
              const hasChildren = r.children && r.children.length > 0
              const k = `${ei}:${r.key}`
              const isOpen = !!open[k]
              return (
                <Box key={r.key} sx={{borderBottom: i < e.rows.length - 1 ? `1px solid ${BORDER}` : 'none'}}>
                  <Box
                    onClick={hasChildren ? () => toggle(k) : undefined}
                    sx={{display: 'grid', gridTemplateColumns: grid, alignItems: 'center', gap: 1.5, px: 2.5, py: 1.4, cursor: hasChildren ? 'pointer' : 'default', '&:hover': {bgcolor: hasChildren ? '#fafbfc' : 'transparent'}}}
                  >
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                      <Box sx={{width: 18, height: 18, borderRadius: '50%', bgcolor: r.color, color: WHITE, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0}}>
                        {hasChildren ? (isOpen ? <RemoveIcon sx={{fontSize: 13}} /> : <AddIcon sx={{fontSize: 13}} />) : <Box sx={{width: 6, height: 6, borderRadius: '50%', bgcolor: WHITE}} />}
                      </Box>
                      <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.semibold, color: NAVY}}>{r.label}</Typography>
                    </Box>
                    {r.cells.map((cell, j) => <Typography key={j} sx={cellSx}>{cellText(cell)}</Typography>)}
                    <Box />
                    <Box />
                  </Box>

                  {hasChildren && (
                    <Collapse in={isOpen}>
                      <Box sx={{bgcolor: '#fcfcfd', borderTop: `1px solid ${BORDER}`}}>
                        {r.children.map((ch) => (
                          <Box key={ch.label} sx={{display: 'grid', gridTemplateColumns: grid, alignItems: 'center', gap: 1.5, px: 2.5, py: 1, borderBottom: `1px dashed ${BORDER}`}}>
                            <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, pl: 3.5}}>{ch.label}</Typography>
                            {ch.cells.map((cell, j) => <Typography key={j} sx={{...cellSx, fontSize: SIZE['2xs'], color: SUBTLE}}>{cellText(cell)}</Typography>)}
                            <Box />
                            <Box />
                          </Box>
                        ))}
                      </Box>
                    </Collapse>
                  )}
                </Box>
              )
            })}
          </Box>
        ))}
      </Box>
    </Box>
  )
}
